import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLE_STYLE_MAP } from '../../constants';
import deltaLogo from '../../assets/logo.png';
import styles from './Navbar.module.css';

function getRoleStyle(key = '') {
  return ROLE_STYLE_MAP[key?.toLowerCase()] ?? { icon: '👤', color: 'rgba(136,153,176,0.1)' };
}

/* ── Human avatar SVG ── */
function HumanAvatar() {
  return (
    <div className={styles.avatar}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" fill="#060912" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#060912" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

/* ── Profile dropdown ── */
function ProfileDropdown({ user, roles, activeRole, onRoleChange, onLogout }) {
  const displayName = user?.username ?? user?.name ?? user?.email?.split('@')[0] ?? 'Trader';
  const multiRole   = roles.length > 1;

  /* parse roles to { key, label } */
  const parsedRoles = roles.map(r =>
    typeof r === 'string'
      ? { key: r.toLowerCase(), label: r }
      : { key: (r.key ?? r.name ?? '').toLowerCase(), label: r.label ?? r.name ?? r.key ?? '' }
  ).filter(r => r.key);

  return (
    <div className={styles.dropdown}>
      <div className={styles.ddHeader}>
        <div className={styles.ddAvatar}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="4" fill="rgba(0,229,160,0.8)" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="rgba(0,229,160,0.8)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <div className={styles.ddName}>{displayName}</div>
          <div className={styles.ddEmail}>{user?.email}</div>
        </div>
      </div>

      {parsedRoles.length > 0 && (
        <>
          <div className={styles.ddSectionLabel}>
            {multiRole ? 'Switch Role' : 'Your Role'}
          </div>
          <div className={styles.roleList}>
            {parsedRoles.map(({ key, label }) => {
              const rs       = getRoleStyle(key);
              const selected = activeRole === key;
              return (
                <div
                  key={key}
                  className={`${styles.roleItem} ${selected ? styles.roleItemSelected : ''}`}
                  onClick={() => multiRole && onRoleChange(key)}
                  style={{ cursor: multiRole ? 'pointer' : 'default' }}
                >
                  <div className={styles.roleIcon} style={{ background: rs.color }}>{rs.icon}</div>
                  <div className={styles.roleText}>
                    <div className={styles.roleName}>
                      {label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}
                    </div>
                  </div>
                  {multiRole && (
                    <div className={`${styles.roleCheck} ${selected ? styles.roleCheckSelected : ''}`}>
                      {selected ? '✓' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className={styles.ddDivider} />
      <div className={`${styles.ddAction} ${styles.ddActionDanger}`} onClick={onLogout}>
        ↩&nbsp; Sign out
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN NAVBAR
══════════════════════════════════════════════ */
export default function Navbar() {
  const { user, activeRole, switchRole, isAdmin, roles, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const ddRef     = useRef(null);
  const [ddOpen,     setDdOpen]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const meData      = user?.data ?? user ?? {};
  const displayName = meData.username ?? meData.name ?? meData.email?.split('@')[0] ?? '';
  const roleBadge   = activeRole ? activeRole.toUpperCase() : '';

  /* close dropdown on outside click */
  useEffect(() => {
    const h = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /* close mobile menu on route change */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className={styles.nav}>
        {/* Brand */}
        <Link to="/dashboard" className={styles.navBrand}>
          <div className={styles.brandLogoWrap}>
            <img src={deltaLogo} alt="DeltaDesk" className={styles.brandLogo} />
          </div>
          <span className={styles.brandName}>DeltaDesk</span>
        </Link>

        {/* Nav links — desktop only */}
        <div className={styles.navLinks}>
          <Link to="/dashboard"  className={`${styles.navLink} ${isActive('/dashboard')  ? styles.navLinkActive : ''}`}>Dashboard</Link>
          <Link to="/orders"     className={`${styles.navLink} ${isActive('/orders')     ? styles.navLinkActive : ''}`}>Orders</Link>
          <Link to="/analytics"  className={`${styles.navLink} ${isActive('/analytics')  ? styles.navLinkActive : ''}`}>Analytics</Link>
          <Link to="/strategy"   className={`${styles.navLink} ${isActive('/strategy')   ? styles.navLinkActive : ''}`}>Strategy</Link>
          {isAdmin && (
            <Link to="/admin"    className={`${styles.navLink} ${isActive('/admin')      ? styles.navLinkActive : ''}`}>Admin</Link>
          )}
        </div>

        {/* Right — hamburger + profile */}
        <div className={styles.navRight} ref={ddRef}>
          {/* Hamburger — mobile only */}
          <button
            className={styles.hamburger}
            onClick={() => { setMobileOpen(o => !o); setDdOpen(false); }}
            aria-label="Toggle navigation"
          >
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </button>

          {/* Profile button */}
          <div className={styles.profileBtn} onClick={() => { setDdOpen(o => !o); setMobileOpen(false); }}>
            <HumanAvatar />
            <div className={styles.profileInfo}>
              <div className={styles.profileName}>{displayName}</div>
              {roleBadge && <div className={styles.profileRole}>{roleBadge}</div>}
            </div>
            <span className={`${styles.chevron} ${ddOpen ? styles.chevronOpen : ''}`}>▼</span>
          </div>

          {ddOpen && (
            <ProfileDropdown
              user={meData}
              roles={roles}
              activeRole={activeRole}
              onRoleChange={(role) => { switchRole(role); setDdOpen(false); }}
              onLogout={handleLogout}
            />
          )}
        </div>
      </nav>

      {/* Mobile navigation menu */}
      {mobileOpen && (
        <div className={styles.mobileMenu}>
          <Link to="/dashboard"  className={`${styles.mobileNavLink} ${isActive('/dashboard')  ? styles.mobileNavLinkActive : ''}`} onClick={() => setMobileOpen(false)}>Dashboard</Link>
          <Link to="/orders"     className={`${styles.mobileNavLink} ${isActive('/orders')     ? styles.mobileNavLinkActive : ''}`} onClick={() => setMobileOpen(false)}>Orders</Link>
          <Link to="/analytics"  className={`${styles.mobileNavLink} ${isActive('/analytics')  ? styles.mobileNavLinkActive : ''}`} onClick={() => setMobileOpen(false)}>Analytics</Link>
          <Link to="/strategy"   className={`${styles.mobileNavLink} ${isActive('/strategy')   ? styles.mobileNavLinkActive : ''}`} onClick={() => setMobileOpen(false)}>Strategy</Link>
          {isAdmin && (
            <Link to="/admin"    className={`${styles.mobileNavLink} ${isActive('/admin')      ? styles.mobileNavLinkActive : ''}`} onClick={() => setMobileOpen(false)}>Admin</Link>
          )}
        </div>
      )}
    </>
  );
}